/**
 * Central helper for figuring out where to send a user after successful
 * authentication. Solves the "You don't have access to this page" race
 * that appears when:
 *   1. An unauthenticated user hits a role-gated route (e.g. /admin/cases).
 *   2. RequireAuth redirects to /login?next=/admin/cases.
 *   3. The user logs in as a customer.
 *   4. Auth.jsx blindly navigates to /admin/cases and the guard renders
 *      the 403 page — even though login succeeded and the user has a
 *      perfectly valid role.
 *
 * The rule enforced here: honour `requestedNext` ONLY when the user's role
 * is allowed to view that path. Otherwise fall back to the role-based
 * default landing page.
 */

// Paths (or path prefixes) that require specific roles. Anything not listed
// here is considered publicly reachable from any authenticated role.
const ROLE_RESTRICTED_PREFIXES = [
  { prefix: "/admin", allowed: ["super_admin"] },
  { prefix: "/business", allowed: ["business", "super_admin"] },
  // /account/* is available to any signed-in user (customer, business, super_admin)
];

function isPathAllowedForRole(path, role) {
  if (!path) return false;
  // Only accept internal, absolute paths — never open redirects.
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  // Public routes that don't need any role check
  const restriction = ROLE_RESTRICTED_PREFIXES.find((r) => path === r.prefix || path.startsWith(r.prefix + "/"));
  if (!restriction) return true;
  return restriction.allowed.includes(role);
}

export function defaultLandingForRole(role) {
  if (role === "super_admin") return "/admin";
  if (role === "business") return "/business";
  return "/account";
}

/**
 * Returns the best post-auth destination for the given user.
 *   - Prefers `requestedNext` when it's role-compatible.
 *   - Otherwise routes to the role default.
 *   - Rejects external URLs and protocol-relative URLs to avoid open redirects.
 */
export function postLoginRedirect(user, requestedNext) {
  const role = user?.role || "customer";
  if (requestedNext && isPathAllowedForRole(requestedNext, role)) {
    return requestedNext;
  }
  return defaultLandingForRole(role);
}
